import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { motion } from "framer-motion";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Button,
  Chip,
  CircularProgress,
  Typography,
  IconButton,
  InputAdornment,
  Stack,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import {
  Search as SearchIcon,
  Event as EventIcon,
  Description as DescriptionIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Pending as PendingIcon,
  ThumbUp as ThumbUpIcon,
  ThumbDown as ThumbDownIcon,
  Block as BlockIcon,
  Person as PersonIcon,
  Badge as BadgeIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import {
  fetchMyLeaves,
  updateLeaveAction,
} from "store/features/company/leaveManagement/leaveDetails/leaveDetailsActions";
import {
  selectLeaves,
  selectLeaveLoading,
  selectUpdatingAction,
} from "store/features/company/leaveManagement/leaveDetails/leaveDetailsSelectors";
import leavesSvg from "assets/folders/leaves.svg";
import { fetchLeaveTypes } from "store/features/company/leaveManagement/leaveType/leaveTypeActions";
import { selectLeaveTypes } from "store/features/company/leaveManagement/leaveType/leaveTypeSelectors";

const LeaveDetails = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const leaves = useSelector(selectLeaves);
  const loading = useSelector(selectLeaveLoading);
  const updatingAction = useSelector(selectUpdatingAction);

  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [leaveToApprove, setLeaveToApprove] = useState(null);
  const [approving, setApproving] = useState(false);

  //colour that i added extra from different api
  const leaveTypes = useSelector(selectLeaveTypes);
  useEffect(() => {
    dispatch(fetchMyLeaves());
    dispatch(fetchLeaveTypes());
  }, [dispatch]);
  const getLeaveTypeColor = (leaveTypeId) => {
    const leaveType = leaveTypes.find((lt) => lt.id === leaveTypeId);
    return leaveType?.color || "#f59e0b";
  };

  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [selectedLeave, setSelectedLeave] = useState(null);
  const [rejectionReason, setRejectionReason] = useState("");

  const filteredLeaves = leaves.filter((leave) => {
    const matchesSearch =
      searchTerm === "" ||
      leave.leave_type_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      leave.reason?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      `${leave.first_name} ${leave.last_name}`
        .toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      leave.employee_code?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus =
      statusFilter === "all" || leave.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      approved: {
        bg: "#d1fae5",
        text: "#065f46",
        darkBg: "rgba(16,185,129,0.25)",
        darkText: "#6ee7b7",
      },
      pending: {
        bg: "#fef3c7",
        text: "#92400e",
        darkBg: "rgba(245,158,11,0.25)",
        darkText: "#fde68a",
      },
      rejected: {
        bg: "#fee2e2",
        text: "#991b1b",
        darkBg: "rgba(239,68,68,0.25)",
        darkText: "#fca5a5",
      },
      cancelled: {
        bg: "#f3f4f6",
        text: "#4b5563",
        darkBg: "rgba(107,114,128,0.25)",
        darkText: "#9ca3af",
      },
    };
    return colors[status] || colors.pending;
  };

  const StatusBadge = ({ status }) => {
    const colors = getStatusColor(status);

    const getIcon = () => {
      switch (status) {
        case "approved":
          return <CheckCircleIcon sx={{ fontSize: 14 }} />;
        case "pending":
          return <PendingIcon sx={{ fontSize: 14 }} />;
        case "rejected":
          return <CancelIcon sx={{ fontSize: 14 }} />;
        case "cancelled":
          return <BlockIcon sx={{ fontSize: 14 }} />;
        default:
          return <PendingIcon sx={{ fontSize: 14 }} />;
      }
    };

    const getLabel = () => {
      return status.charAt(0).toUpperCase() + status.slice(1);
    };

    return (
      <Chip
        icon={getIcon()}
        label={getLabel()}
        size="small"
        sx={{
          backgroundColor: colors.bg,
          color: colors.text,
          fontWeight: 500,
          fontSize: "0.75rem",
          height: "24px",
          ...(theme.palette.mode === "dark" && {
            backgroundColor: colors.darkBg,
            color: colors.darkText,
          }),
        }}
      />
    );
  };
  const formatDays = (days) => {
    const num = parseFloat(days);
    if (isNaN(num)) return "0 days";

    const wholeDays = Math.floor(num);
    const fraction = num - wholeDays;

    if (fraction === 0) {
      return `${wholeDays} ${wholeDays === 1 ? "day" : "days"}`;
    }

    if (fraction === 0.5) {
      if (wholeDays === 0) return "½ day";
      return `${wholeDays} ½ days`;
    }

    // For other fractions like 0.25, 0.75, etc.
    if (fraction === 0.25) {
      if (wholeDays === 0) return "¼ day";
      return `${wholeDays} ¼ days`;
    }

    if (fraction === 0.75) {
      if (wholeDays === 0) return "¾ day";
      return `${wholeDays} ¾ days`;
    }

    return `${num} days`;
  };
  const HalfDayBadge = ({ isHalfDay, session }) => {
    if (!isHalfDay) return null;

    return (
      <Chip
        label={session === "first_half" ? "First Half" : "Second Half"}
        size="small"
        sx={{
          backgroundColor: "#dbeafe",
          color: "#1e40af",
          fontWeight: 500,
          fontSize: "0.7rem",
          height: "20px",
          ...(theme.palette.mode === "dark" && {
            backgroundColor: "rgba(59,130,246,0.25)",
            color: "#93c5fd",
          }),
        }}
      />
    );
  };
  const handleApproveClick = (leave) => {
    setLeaveToApprove(leave);
    setApproveDialogOpen(true);
  };

  const handleApproveConfirm = async () => {
    if (leaveToApprove) {
      setApproving(true);
      const result = await dispatch(
        updateLeaveAction(leaveToApprove.id, { action: "approved" }),
      );
      if (result.success) {
        setApproveDialogOpen(false);
        setLeaveToApprove(null);
      }
      setApproving(false);
    }
  };

  const handleApproveCancel = () => {
    setApproveDialogOpen(false);
    setLeaveToApprove(null);
  };

  const handleRejectClick = (leave) => {
    setSelectedLeave(leave);
    setRejectionReason("");
    setRejectDialogOpen(true);
  };

  const handleRejectConfirm = async () => {
    if (selectedLeave && rejectionReason.trim()) {
      const result = await dispatch(
        updateLeaveAction(selectedLeave.id, {
          action: "rejected",
          rejection_reason: rejectionReason,
        }),
      );
      if (result.success) {
        setRejectDialogOpen(false);
        setSelectedLeave(null);
        setRejectionReason("");
      }
    }
  };

  const handleRejectCancel = () => {
    setRejectDialogOpen(false);
    setSelectedLeave(null);
    setRejectionReason("");
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background:
          theme.palette.mode === "dark"
            ? "linear-gradient(135deg, #0a0f1e 0%, #0f172a 50%, #1e1b2e 100%)"
            : "linear-gradient(135deg, #fffbeb 0%, #ffffff 50%, #fefce8 100%)",
        p: 3,
      }}
    >
      {/* Header Section */}
      <Box sx={{ mb: 4 }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems={{ sm: "center" }}
          justifyContent="space-between"
          sx={{ width: "100%" }}
        >
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            sx={{ flex: 1 }}
          >
            <Box
              component="img"
              src={leavesSvg}
              alt="Leaves"
              sx={{
                width: { xs: 64, lg: 80 },
                height: { xs: 64, lg: 80 },
                objectFit: "contain",
              }}
            />
            <Box>
              <Typography
                variant="h4"
                component="h1"
                sx={{
                  fontWeight: "bold",
                  color: theme.palette.mode === "dark" ? "#ffffff" : "#111827",
                }}
              >
                Leave Details
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.mode === "dark" ? "#9ca3af" : "#6b7280",
                  mt: 0.5,
                }}
              >
                View and manage all leave requests from employees
              </Typography>
            </Box>
          </Stack>
        </Stack>
      </Box>

      {/* Search and Filter Section */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        alignItems={{ sm: "center" }}
        justifyContent="space-between"
        sx={{ mb: 3 }}
      >
        <TextField
          placeholder="Search by employee name, code, leave type or reason..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          variant="outlined"
          size="small"
          sx={{
            flex: 1,
            maxWidth: { sm: 400 },
            "& .MuiOutlinedInput-root": {
              borderRadius: "12px",
              backgroundColor:
                theme.palette.mode === "dark" ? "#1e293b" : "#ffffff",
              "& fieldset": {
                borderColor:
                  theme.palette.mode === "dark" ? "#334155" : "#e5e7eb",
              },
              "&:hover fieldset": {
                borderColor: "#f59e0b",
              },
              "&.Mui-focused fieldset": {
                borderColor: "#f59e0b",
                borderWidth: "2px",
              },
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: "#9ca3af" }} />
              </InputAdornment>
            ),
          }}
        />
        <Stack direction="row" spacing={1}>
          {["all", "pending", "approved", "rejected", "cancelled"].map(
            (filter) => (
              <Button
                key={filter}
                variant={statusFilter === filter ? "contained" : "outlined"}
                onClick={() => setStatusFilter(filter)}
                sx={{
                  textTransform: "capitalize",
                  borderRadius: "8px",
                  px: 2,
                  py: 0.7,
                  fontSize: "0.875rem",
                  fontWeight: 500,
                  ...(statusFilter === filter && {
                    background:
                      "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                    boxShadow: "0 4px 6px -1px rgba(245, 158, 11, 0.25)",
                    "&:hover": {
                      background:
                        "linear-gradient(135deg, #d97706 0%, #b45309 100%)",
                    },
                  }),
                  ...(statusFilter !== filter && {
                    borderColor:
                      theme.palette.mode === "dark" ? "#475569" : "#d1d5db",
                    color:
                      theme.palette.mode === "dark" ? "#d1d5db" : "#6b7280",
                    "&:hover": {
                      borderColor: "#f59e0b",
                      backgroundColor:
                        theme.palette.mode === "dark"
                          ? "rgba(245,158,11,0.1)"
                          : "#fffbeb",
                    },
                  }),
                }}
              >
                {filter.charAt(0).toUpperCase() + filter.slice(1)}
              </Button>
            ),
          )}
        </Stack>
      </Stack>

      {/* Loading State */}
      {loading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: 400,
          }}
        >
          <Stack alignItems="center" spacing={2}>
            <CircularProgress
              size={48}
              sx={{
                color: "#f59e0b",
                "& .MuiCircularProgress-circle": {
                  strokeLinecap: "round",
                },
              }}
            />
            <Typography
              sx={{
                color: theme.palette.mode === "dark" ? "#9ca3af" : "#6b7280",
              }}
            >
              Loading leave requests...
            </Typography>
          </Stack>
        </Box>
      ) : (
        <Paper
          elevation={0}
          sx={{
            borderRadius: "16px",
            overflow: "hidden",
            boxShadow:
              theme.palette.mode === "dark"
                ? "0 10px 25px -5px rgba(0,0,0,0.3)"
                : "0 10px 25px -5px rgba(0,0,0,0.05), 0 8px 10px -6px rgba(0,0,0,0.02)",
            border: `1px solid ${
              theme.palette.mode === "dark" ? "#1e293b" : "#f3f4f6"
            }`,
          }}
        >
          <TableContainer sx={{ overflowX: "auto" }}>
            <Table sx={{ minWidth: 1000 }}>
              <TableHead
                sx={{
                  backgroundColor:
                    theme.palette.mode === "dark" ? "#1e293b" : "#f9fafb",
                }}
              >
                <TableRow>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.75rem",
                      color:
                        theme.palette.mode === "dark" ? "#9ca3af" : "#6b7280",
                    }}
                  >
                    ID / Employee ID
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.75rem",
                      color:
                        theme.palette.mode === "dark" ? "#9ca3af" : "#6b7280",
                    }}
                  >
                    Employee
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.75rem",
                      color:
                        theme.palette.mode === "dark" ? "#9ca3af" : "#6b7280",
                    }}
                  >
                    Leave Type
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.75rem",
                      color:
                        theme.palette.mode === "dark" ? "#9ca3af" : "#6b7280",
                    }}
                  >
                    Duration
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.75rem",
                      color:
                        theme.palette.mode === "dark" ? "#9ca3af" : "#6b7280",
                    }}
                  >
                    Days
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.75rem",
                      color:
                        theme.palette.mode === "dark" ? "#9ca3af" : "#6b7280",
                    }}
                  >
                    Reason
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.75rem",
                      color:
                        theme.palette.mode === "dark" ? "#9ca3af" : "#6b7280",
                    }}
                  >
                    Status
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.75rem",
                      color:
                        theme.palette.mode === "dark" ? "#9ca3af" : "#6b7280",
                    }}
                  >
                    Applied On
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.75rem",
                      color:
                        theme.palette.mode === "dark" ? "#9ca3af" : "#6b7280",
                    }}
                  >
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredLeaves.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                      <EventIcon
                        sx={{ fontSize: 48, color: "#d1d5db", mb: 1 }}
                      />
                      <Typography
                        sx={{
                          color:
                            theme.palette.mode === "dark"
                              ? "#9ca3af"
                              : "#6b7280",
                        }}
                      >
                        No leave requests found.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredLeaves.map((leave, index) => (
                    <TableRow
                      key={leave.id}
                      component={motion.tr}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.05 }}
                      sx={{
                        "&:hover": {
                          backgroundColor:
                            theme.palette.mode === "dark"
                              ? "rgba(30,41,59,0.5)"
                              : "#f9fafb",
                        },
                      }}
                    >
                      {/* ID and Employee ID */}
                      <TableCell>
                        <Stack spacing={0.5}>
                          <Typography
                            sx={{
                              fontFamily: "monospace",
                              fontWeight: 700,
                              fontSize: "0.875rem",
                              color: "#f59e0b",
                            }}
                          >
                            #{leave.id}
                          </Typography>
                          <Typography
                            sx={{
                              fontFamily: "monospace",
                              fontSize: "0.7rem",
                              color:
                                theme.palette.mode === "dark"
                                  ? "#9ca3af"
                                  : "#6b7280",
                            }}
                          >
                            Emp ID: {leave.employee_id}
                          </Typography>
                        </Stack>
                      </TableCell>
                      {/* Employee Name & Code Column */}
                      <TableCell>
                        <Stack
                          direction="row"
                          spacing={1.5}
                          alignItems="center"
                        >
                          <Box>
                            <Typography
                              sx={{
                                fontWeight: 500,
                                fontSize: "0.875rem",
                                color:
                                  theme.palette.mode === "dark"
                                    ? "#ffffff"
                                    : "#111827",
                              }}
                            >
                              {leave.first_name} {leave.last_name}
                            </Typography>
                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="center"
                            >
                              <BadgeIcon
                                sx={{ fontSize: 12, color: "#9ca3af" }}
                              />
                              <Typography
                                sx={{ fontSize: "0.7rem", color: "#9ca3af" }}
                              >
                                {leave.employee_code}
                              </Typography>
                            </Stack>
                          </Box>
                        </Stack>
                      </TableCell>
                      {/* Leave Type Column */}
                      <TableCell>
                        <Stack
                          direction="row"
                          spacing={1.5}
                          alignItems="center"
                        >
                          <Stack alignItems="center" spacing={0.5}>
                            <Box
                              sx={{
                                width: 32,
                                height: 32,
                                borderRadius: "8px",
                                backgroundColor: getLeaveTypeColor(
                                  leave.leave_type_id,
                                ),
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <EventIcon
                                sx={{ fontSize: 16, color: "#ffffff" }}
                              />
                            </Box>
                            <Chip
                              label={`ID: ${leave.leave_type_id}`}
                              size="small"
                              sx={{
                                height: "18px",
                                fontSize: "0.6rem",
                                fontFamily: "monospace",
                                fontWeight: 500,
                                backgroundColor: getLeaveTypeColor(
                                  leave.leave_type_id,
                                ),
                                color: "#ffffff",
                                "& .MuiChip-label": {
                                  px: 0.8,
                                  py: 0,
                                },
                              }}
                            />
                          </Stack>
                          <Typography
                            sx={{
                              fontWeight: 500,
                              fontSize: "0.875rem",
                              color:
                                theme.palette.mode === "dark"
                                  ? "#ffffff"
                                  : "#111827",
                            }}
                          >
                            {leave.leave_type_name}
                          </Typography>
                        </Stack>
                      </TableCell>

                      {/* Duration Column */}
                      <TableCell>
                        <Stack spacing={0.5}>
                          <Typography
                            sx={{
                              fontSize: "0.75rem",
                              color:
                                theme.palette.mode === "dark"
                                  ? "#d1d5db"
                                  : "#4b5563",
                            }}
                          >
                            <b>From: </b> {formatDate(leave.start_date)}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "0.75rem",
                              color:
                                theme.palette.mode === "dark"
                                  ? "#d1d5db"
                                  : "#4b5563",
                            }}
                          >
                            <b>To: </b> {formatDate(leave.end_date)}
                          </Typography>
                          {leave.is_half_day === 1 && (
                            <HalfDayBadge
                              isHalfDay={leave.is_half_day === 1}
                              session={leave.half_day_session}
                            />
                          )}
                        </Stack>
                      </TableCell>

                      {/* Days Column */}
                      <TableCell>
                        <Typography
                          sx={{
                            fontSize: "0.875rem",
                            fontWeight: 600,
                            color:
                              theme.palette.mode === "dark"
                                ? "#ffffff"
                                : "#111827",
                          }}
                        >
                          {parseFloat(leave.days).toString()}{" "}
                        </Typography>
                      </TableCell>

                      {/* Reason Column */}
                      <TableCell>
                        <Typography
                          sx={{
                            fontSize: "0.875rem",
                            color:
                              theme.palette.mode === "dark"
                                ? "#d1d5db"
                                : "#4b5563",
                            fontStyle: "italic",
                            maxWidth: 200,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                          title={leave.reason}
                        >
                          <span style={{ color: "#3b82f6", fontWeight: 500 }}>
                            "
                          </span>
                          {leave.reason || "-"}
                          <span style={{ color: "#3b82f6", fontWeight: 500 }}>
                            "
                          </span>
                        </Typography>
                      </TableCell>

                      {/* Status Column */}
                      <TableCell>
                        <StatusBadge status={leave.status} />
                      </TableCell>

                      {/* Applied On Column */}
                      <TableCell>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <EventIcon sx={{ fontSize: 14, color: "#f59e0b" }} />
                          <Typography
                            sx={{
                              fontSize: "0.875rem",
                              color:
                                theme.palette.mode === "dark"
                                  ? "#d1d5db"
                                  : "#4b5563",
                            }}
                          >
                            {formatDateTime(leave.created_at)}
                          </Typography>
                        </Stack>
                      </TableCell>

                      {/* Actions Column */}
                      <TableCell>
                        {leave.status === "pending" && (
                          <Stack direction="row" spacing={1}>
                            <Button
                              size="small"
                              variant="contained"
                              onClick={() => handleApproveClick(leave)}
                              disabled={updatingAction}
                              startIcon={<ThumbUpIcon sx={{ fontSize: 16 }} />}
                              sx={{
                                backgroundColor: "#10b981",
                                textTransform: "none",
                                fontSize: "0.75rem",
                                fontWeight: 500,
                                borderRadius: "8px",
                                "&:hover": {
                                  backgroundColor: "#059669",
                                },
                              }}
                            >
                              Approve
                            </Button>
                            <Button
                              size="small"
                              variant="contained"
                              onClick={() => handleRejectClick(leave)}
                              disabled={updatingAction}
                              startIcon={
                                <ThumbDownIcon sx={{ fontSize: 16 }} />
                              }
                              sx={{
                                backgroundColor: "#ef4444",
                                textTransform: "none",
                                fontSize: "0.75rem",
                                fontWeight: 500,
                                borderRadius: "8px",
                                "&:hover": {
                                  backgroundColor: "#dc2626",
                                },
                              }}
                            >
                              Reject
                            </Button>
                          </Stack>
                        )}
                        {leave.status !== "pending" && (
                          <Typography
                            sx={{
                              fontSize: "0.75rem",
                              color:
                                leave.status === "approved"
                                  ? "#22c55e"
                                  : theme.palette.mode === "dark"
                                  ? "#6b7280"
                                  : "#9ca3af",
                              fontStyle: "italic",
                            }}
                          >
                            {leave.status === "approved" && "Approved"}
                            {leave.status === "rejected" && "Rejected"}
                            {leave.status === "cancelled" && "Cancelled"}
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Leave Count Summary */}
      {!loading && leaves.length > 0 && (
        <Stack
          direction="row"
          justifyContent="space-between"
          sx={{
            mt: 2,
            gap: 2,
            fontSize: "0.875rem",
            color: theme.palette.mode === "dark" ? "#9ca3af" : "#6b7280",
          }}
        >
          <Typography variant="body2">
            Total Leave Requests: {leaves.length}
          </Typography>
          <Typography variant="body2">
            Showing: {filteredLeaves.length} of {leaves.length}
          </Typography>
        </Stack>
      )}

      {/* Empty State for No Search Results */}
      {!loading && filteredLeaves.length === 0 && leaves.length > 0 && (
        <Paper
          sx={{
            mt: 4,
            p: 4,
            textAlign: "center",
            borderRadius: "16px",
            backgroundColor:
              theme.palette.mode === "dark" ? "#1e293b" : "#ffffff",
          }}
        >
          <SearchIcon sx={{ fontSize: 48, color: "#d1d5db", mb: 1 }} />
          <Typography
            sx={{
              color: theme.palette.mode === "dark" ? "#9ca3af" : "#6b7280",
              mb: 2,
            }}
          >
            No leave requests match your search criteria
          </Typography>
          <Button
            onClick={() => {
              setSearchTerm("");
              setStatusFilter("all");
            }}
            sx={{
              color: "#f59e0b",
              textTransform: "none",
              "&:hover": {
                backgroundColor:
                  theme.palette.mode === "dark"
                    ? "rgba(245,158,11,0.1)"
                    : "#fffbeb",
              },
            }}
          >
            Clear filters
          </Button>
        </Paper>
      )}

      {/* Rejection Dialog */}
      <Dialog
        open={rejectDialogOpen}
        onClose={handleRejectCancel}
        maxWidth="xs"
        fullWidth
        TransitionComponent={motion.div}
        TransitionProps={{
          timeout: 300,
          style: { overflow: "visible" },
        }}
        sx={{
          "& .MuiDialog-paper": {
            borderRadius: "20px",
            overflow: "hidden",
            backgroundColor:
              theme.palette.mode === "dark" ? "#0f172a" : "#ffffff",
            maxWidth: "400px",
            margin: "16px",
            boxShadow: theme.shadows[24],
            position: "relative",
          },
        }}
        slotProps={{
          backdrop: {
            sx: {
              backgroundColor: "rgba(0, 0, 0, 0.6)",
              backdropFilter: "blur(4px)",
            },
          },
        }}
      >
        <motion.div
          initial={{ x: "100%", opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          exit={{ x: "100%", opacity: 0 }}
          transition={{ type: "spring", damping: 25, stiffness: 200 }}
        >
          {/* Red Banner for Reject */}
          <Box
            sx={{
              background: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
              height: "6px",
            }}
          />

          <Box sx={{ p: 3, position: "relative" }}>
            <IconButton
              onClick={handleRejectCancel}
              sx={{
                position: "absolute",
                top: 16,
                right: 16,
                width: 32,
                height: 32,
                borderRadius: "8px",
                color: theme.palette.mode === "dark" ? "#9ca3af" : "#6b7280",
                "&:hover": {
                  backgroundColor: "#ef4444",
                  color: "#ffffff",
                },
              }}
            >
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>

            <Box
              sx={{
                width: 80,
                height: 80,
                margin: "0 auto",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mb: 2,
                backgroundColor: "#fee2e2",
                borderRadius: "50%",
              }}
            >
              <ThumbDownIcon sx={{ fontSize: 40, color: "#ef4444" }} />
            </Box>

            <Typography
              sx={{
                fontWeight: 700,
                fontSize: "1.25rem",
                color: theme.palette.mode === "dark" ? "#ffffff" : "#111827",
                textAlign: "center",
                mb: 2,
              }}
            >
              Reject Leave Request
            </Typography>

            <Typography
              sx={{
                color: theme.palette.mode === "dark" ? "#d1d5db" : "#4b5563",
                fontSize: "0.95rem",
                lineHeight: 1.5,
                textAlign: "center",
                mb: 1,
              }}
            >
              Are you sure you want to reject this leave request from
            </Typography>

            <Typography
              sx={{
                fontWeight: 600,
                color: "#ef4444",
                fontSize: "1rem",
                textAlign: "center",
                mb: 2,
                wordBreak: "break-word",
              }}
            >
              "{selectedLeave?.first_name} {selectedLeave?.last_name}"
            </Typography>

            <TextField
              autoFocus
              multiline
              rows={3}
              fullWidth
              variant="outlined"
              label="Rejection Reason"
              placeholder="Please provide a reason for rejection..."
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              required
              sx={{
                mb: 2,
                "& .MuiOutlinedInput-root": {
                  borderRadius: "12px",
                  backgroundColor:
                    theme.palette.mode === "dark" ? "#0f172a" : "#f9fafb",
                },
              }}
            />
          </Box>

          <Box sx={{ p: 3, pt: 0, display: "flex", gap: 2 }}>
            <Button
              onClick={handleRejectCancel}
              disabled={updatingAction}
              fullWidth
              sx={{
                textTransform: "none",
                fontWeight: 600,
                borderRadius: "12px",
                py: 1,
                fontSize: "0.875rem",
                color: theme.palette.mode === "dark" ? "#d1d5db" : "#4b5563",
                backgroundColor:
                  theme.palette.mode === "dark" ? "#1e293b" : "#f3f4f6",
                border: `1px solid ${
                  theme.palette.mode === "dark" ? "#334155" : "#e5e7eb"
                }`,
                "&:hover": {
                  backgroundColor:
                    theme.palette.mode === "dark" ? "#334155" : "#e5e7eb",
                },
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleRejectConfirm}
              disabled={!rejectionReason.trim() || updatingAction}
              fullWidth
              variant="contained"
              sx={{
                textTransform: "none",
                fontWeight: 600,
                borderRadius: "12px",
                py: 1,
                fontSize: "0.875rem",
                background: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
                color: "#ffffff",
                boxShadow: "0 4px 12px rgba(239, 68, 68, 0.3)",
                "&:hover": {
                  background:
                    "linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)",
                  boxShadow: "0 6px 16px rgba(239, 68, 68, 0.4)",
                  transform: "translateY(-1px)",
                },
                "&:active": {
                  transform: "translateY(0)",
                },
                "&:disabled": {
                  background:
                    "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
                  opacity: 0.6,
                },
              }}
            >
              {updatingAction ? (
                <CircularProgress size={20} sx={{ color: "#ffffff" }} />
              ) : (
                "Confirm Rejection"
              )}
            </Button>
          </Box>
        </motion.div>
      </Dialog>
      {/* Approve Confirmation Dialog */}
      <Dialog
        open={approveDialogOpen}
        onClose={handleApproveCancel}
        maxWidth="xs"
        fullWidth
        TransitionComponent={motion.div}
        TransitionProps={{
          timeout: 300,
          style: { overflow: "visible" },
        }}
        sx={{
          "& .MuiDialog-paper": {
            borderRadius: "20px",
            overflow: "hidden",
            backgroundColor:
              theme.palette.mode === "dark" ? "#0f172a" : "#ffffff",
            maxWidth: "400px",
            margin: "16px",
            boxShadow: theme.shadows[24],
            position: "relative",
          },
        }}
        slotProps={{
          backdrop: {
            sx: {
              backgroundColor: "rgba(0, 0, 0, 0.6)",
              backdropFilter: "blur(4px)",
            },
          },
        }}
      >
        <motion.div
          initial={{ x: "100%", opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          exit={{ x: "100%", opacity: 0 }}
          transition={{ type: "spring", damping: 25, stiffness: 200 }}
        >
          {/* Green Banner for Approve */}
          <Box
            sx={{
              background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
              height: "6px",
            }}
          />

          <Box sx={{ p: 3, position: "relative" }}>
            {/* Close Icon Button */}
            <IconButton
              onClick={handleApproveCancel}
              sx={{
                position: "absolute",
                top: 16,
                right: 16,
                width: 32,
                height: 32,
                borderRadius: "8px",
                color: theme.palette.mode === "dark" ? "#9ca3af" : "#6b7280",
                "&:hover": {
                  backgroundColor: "#10b981",
                  color: "#ffffff",
                },
              }}
            >
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>

            {/* SVG Icon - You can use a checkmark or approval icon */}
            <Box
              sx={{
                width: 80,
                height: 80,
                margin: "0 auto",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mb: 2,
                backgroundColor: "#d1fae5",
                borderRadius: "50%",
              }}
            >
              <ThumbUpIcon sx={{ fontSize: 40, color: "#10b981" }} />
            </Box>

            <Typography
              sx={{
                fontWeight: 700,
                fontSize: "1.25rem",
                color: theme.palette.mode === "dark" ? "#ffffff" : "#111827",
                textAlign: "center",
                mb: 2,
              }}
            >
              Approve Leave Request
            </Typography>

            <Typography
              sx={{
                color: theme.palette.mode === "dark" ? "#d1d5db" : "#4b5563",
                fontSize: "0.95rem",
                lineHeight: 1.5,
                textAlign: "center",
                mb: 1,
              }}
            >
              Are you sure you want to approve this leave request from
            </Typography>

            <Typography
              sx={{
                fontWeight: 600,
                color: "#10b981",
                fontSize: "1rem",
                textAlign: "center",
                mb: 2,
                wordBreak: "break-word",
              }}
            >
              "{leaveToApprove?.first_name} {leaveToApprove?.last_name}"
            </Typography>

            <Typography
              sx={{
                color: theme.palette.mode === "dark" ? "#9ca3af" : "#6b7280",
                fontSize: "0.85rem",
                lineHeight: 1.4,
                textAlign: "center",
              }}
            >
              <b>Type: </b> {leaveToApprove?.leave_type_name}
              <br />
              <b>Duration: </b>
              {leaveToApprove?.days} days
            </Typography>
          </Box>

          <Box sx={{ p: 3, pt: 0, display: "flex", gap: 2 }}>
            <Button
              onClick={handleApproveCancel}
              disabled={approving}
              fullWidth
              sx={{
                textTransform: "none",
                fontWeight: 600,
                borderRadius: "12px",
                py: 1,
                fontSize: "0.875rem",
                color: theme.palette.mode === "dark" ? "#d1d5db" : "#4b5563",
                backgroundColor:
                  theme.palette.mode === "dark" ? "#1e293b" : "#f3f4f6",
                border: `1px solid ${
                  theme.palette.mode === "dark" ? "#334155" : "#e5e7eb"
                }`,
                "&:hover": {
                  backgroundColor:
                    theme.palette.mode === "dark" ? "#334155" : "#e5e7eb",
                },
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleApproveConfirm}
              disabled={approving}
              fullWidth
              variant="contained"
              sx={{
                textTransform: "none",
                fontWeight: 600,
                borderRadius: "12px",
                py: 1,
                fontSize: "0.875rem",
                background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                color: "#ffffff",
                boxShadow: "0 4px 12px rgba(16, 185, 129, 0.3)",
                "&:hover": {
                  background:
                    "linear-gradient(135deg, #059669 0%, #047857 100%)",
                  boxShadow: "0 6px 16px rgba(16, 185, 129, 0.4)",
                  transform: "translateY(-1px)",
                },
                "&:active": {
                  transform: "translateY(0)",
                },
                "&:disabled": {
                  background:
                    "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                  opacity: 0.6,
                },
              }}
            >
              {approving ? (
                <CircularProgress size={20} sx={{ color: "#ffffff" }} />
              ) : (
                "Approve"
              )}
            </Button>
          </Box>
        </motion.div>
      </Dialog>
    </Box>
  );
};

export default LeaveDetails;
